import React, { useEffect, useState } from 'react';
import { reviewsService } from '@/services/customer.service';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StarRating } from '@/components/customer/StarRating';
import { Quote, Star, MessageSquareText } from 'lucide-react';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
}

/**
 * Recensioni pubbliche delle escursioni — mostrate SOTTO la hero della pagina Parchi.
 * Non tocca i componenti mappa. Si nasconde se non ci sono ancora recensioni.
 */
export function ParkReviews() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    reviewsService.getParkReviews(12)
      .then((d) => { if (active) setData(d); })
      .catch(() => { if (active) setData({ items: [], count: 0 }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <section className="bg-muted/30 py-16">
        <div className="mx-auto max-w-6xl px-4">
          <Skeleton className="mx-auto mb-8 h-8 w-72" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-2xl" />)}
          </div>
        </div>
      </section>
    );
  }

  if (!data || !data.count) return null;

  return (
    <section className="border-t border-border bg-muted/30 py-16" data-testid="park-reviews">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-10 flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <MessageSquareText className="h-3.5 w-3.5" />Dicono di noi
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Recensioni delle escursioni
          </h2>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 shadow-sm">
              <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
              <span className="text-lg font-bold text-foreground" data-testid="park-reviews-avg">{data.avg_overall}</span>
              <span className="text-sm text-muted-foreground">su 5</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {data.count} recension{data.count === 1 ? 'e' : 'i'} verificat{data.count === 1 ? 'a' : 'e'}
            </span>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {data.items.map((r) => (
            <Card key={r.id} className="relative flex flex-col p-5" data-testid={`park-review-${r.id}`}>
              <Quote className="absolute right-4 top-4 h-8 w-8 text-primary/10" />
              <StarRating value={r.rating} size="sm" className="mb-3" />
              {r.comment && (
                <p className="flex-1 text-sm leading-relaxed text-foreground/90">“{r.comment}”</p>
              )}
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-sm font-semibold text-foreground">{r.display_name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {r.activity_title}{r.operator_name ? ` · Guida ${r.operator_name}` : ''} · {fmt(r.created_at)}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
